import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/api/client";
import { toast } from "sonner";
import {
  Check,
  AlertCircle,
  Loader2,
  Copy,
  Globe,
  Info,
  ExternalLink,
  RefreshCw,
  Unlink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function DomainTab({ space, onReload }) {
  const [subdomain, setSubdomain] = useState(space.domain?.subdomain || "");
  const [customDomain, setCustomDomain] = useState(space.domain?.custom_domain || "");
  const [domainMeta, setDomainMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [removing, setRemoving] = useState(false);

  const loadDomainMeta = useCallback(async () => {
    try {
      const { data } = await api.get(`/spaces/${space.id}/domain`);
      setDomainMeta(data);
      if (data.subdomain) setSubdomain(data.subdomain);
      setCustomDomain(data.custom_domain || "");
    } catch (e) {
      console.error(formatError(e));
    }
  }, [space.id]);

  useEffect(() => {
    loadDomainMeta();
  }, [loadDomainMeta, space.id]);

  const baseDomain = domainMeta?.base_domain || "handletech.io";
  const cnameTarget = domainMeta?.cname_target || "";
  const scheme = domainMeta?.public_url_scheme || "https";
  const docsPublicUrl = customDomain ? `${scheme}://${customDomain}` : "";
  const dnsHost = customDomain.includes(".") ? customDomain.split(".")[0] : customDomain;

  const saveSub = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/spaces/${space.id}/domain`, { subdomain });
      setDomainMeta(data);
      toast.success("Subdomain saved");
      onReload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveCustom = async (domainValue) => {
    const value = (domainValue ?? customDomain).trim().toLowerCase();
    if (!value) {
      toast.error("Please enter a domain");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/spaces/${space.id}/domain`, { custom_domain: value });
      setCustomDomain(value);
      setDomainMeta(data);
      setShowSetup(false);
      setNewDomain("");
      setVerifyResult(null);
      toast.success("Custom domain saved. Configure DNS below.");
      onReload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/spaces/${space.id}/domain/verify`);
      setVerifyResult(data);
      if (data.verified) {
        toast.success(data.message || "Domain verified!");
      } else {
        toast.error(data.message || "DNS not pointing correctly yet");
      }
      await loadDomainMeta();
      onReload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setVerifying(false);
    }
  };

  const removeCustom = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete(`/spaces/${space.id}/domain/custom`);
      setCustomDomain("");
      setDomainMeta(data);
      setVerifyResult(null);
      toast.success("Custom domain unlinked");
      onReload();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRemoving(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-2xl font-black tracking-tight mb-6">Domain</h2>

      <section className="bg-white border border-zinc-200 rounded-sm p-6 mb-6">
        <h3 className="font-bold mb-3">Subdomain</h3>
        <p className="text-sm text-zinc-500 mb-4">
          Preview path on the admin host (before custom domain is verified).
        </p>
        <div className="flex items-center gap-2">
          <input
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value)}
            data-testid="subdomain-input"
            className="flex-1 px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]"
          />
          <span className="text-sm text-zinc-500">.{baseDomain}</span>
          <button
            onClick={saveSub}
            disabled={saving}
            data-testid="subdomain-save"
            className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50"
          >
            Save
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Preview: <code className="bg-zinc-100 px-1.5 py-0.5 rounded-sm">/s/{space.slug}</code>
        </p>
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Custom domain
            </h3>
            <p className="text-sm text-zinc-500">
              Link your own hostname (e.g. <code>help.handletech.io</code>) to this space&apos;s public docs.
            </p>
          </div>
          {!customDomain && (
            <Button onClick={() => setShowSetup(true)} data-testid="setup-custom-domain-btn">
              Set up custom domain
            </Button>
          )}
        </div>

        {customDomain && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono font-medium">{customDomain}</span>
              {space.domain?.custom_domain_verified ? (
                <span className="text-emerald-700 bg-emerald-100 px-2 py-1 rounded-sm text-xs font-bold flex items-center gap-1">
                  <Check className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="text-amber-700 bg-amber-100 px-2 py-1 rounded-sm text-xs font-bold">
                  Not verified
                </span>
              )}
            </div>

            {space.domain?.custom_domain_verified && docsPublicUrl && (
              <div className="flex flex-wrap items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <Check className="w-4 h-4 shrink-0" />
                <span className="font-medium">Public documentation:</span>
                <a
                  href={docsPublicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-mono underline underline-offset-2"
                >
                  {docsPublicUrl}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {!space.domain?.custom_domain_verified && cnameTarget && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                <h4 className="font-semibold text-amber-900 flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4" />
                  DNS configuration required
                </h4>
                <p className="text-sm text-amber-800 mb-3">
                  After DNS propagates, visitors open{" "}
                  <span className="font-mono font-medium">{docsPublicUrl}</span>
                  — the same published documentation for this space, on your own hostname. Point your
                  subdomain to the <strong>same servers</strong> as this Handle Docs deployment (value below).
                </p>
                <p className="text-sm text-amber-800 mb-4">
                  In your DNS provider, add a <strong>CNAME</strong> from your subdomain to this target:
                </p>
                <div className="bg-white border border-amber-100 rounded-sm p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">Type</div>
                    <div className="font-mono">CNAME</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">Name / Host</div>
                    <div className="font-mono">{dnsHost}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">Value / Target</div>
                    <div className="font-mono flex items-center gap-2">
                      {cnameTarget}
                      <button
                        type="button"
                        onClick={() => copyText(cnameTarget)}
                        className="text-zinc-400 hover:text-zinc-900"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={verify}
                disabled={verifying}
                data-testid="verify-domain-btn"
                className="border border-zinc-300 hover:bg-zinc-50 px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Verify DNS configuration
              </button>
              {verifyResult && !verifyResult.verified && (
                <span className="text-rose-700 bg-rose-100 px-2 py-1 rounded-sm text-xs font-bold flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {verifyResult.status}
                </span>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={removing}
                    data-testid="unlink-custom-domain-btn"
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  >
                    {removing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Unlink domain
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unlink custom domain?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This removes <span className="font-mono font-medium">{customDomain}</span> from this
                      space. Visitors will no longer open your docs on that hostname. You can link a
                      different domain later from this page.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={removeCustom}
                      className="bg-rose-600 hover:bg-rose-700"
                      data-testid="confirm-unlink-domain-btn"
                    >
                      Unlink domain
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </section>

      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set up custom domain</DialogTitle>
            <DialogDescription>
              Enter the full hostname where this space&apos;s documentation should be published.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value.toLowerCase().trim())}
            placeholder="help.handletech.io"
            data-testid="custom-domain-input"
            className="font-mono"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetup(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveCustom(newDomain)} disabled={saving} data-testid="custom-domain-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
